import { type ComputedRef, computed, reactive } from 'vue'
import { strapi } from '@strapi/client'
import {
  StrapiComponentName,
  type Block,
  type BlockList,
  type CuratedProject,
  type Page,
  type ProfileList,
  type Project,
  type Resource,
  type ResourceGroup,
  type ResourceList,
} from '@/models'
import type { LocaleCode } from '@/i18n'

const client = strapi({
  baseURL: import.meta.env.VITE_STRAPI_URL + '/api',
  auth: import.meta.env.VITE_STRAPI_READ_ONLY_TOKEN,
})

interface State {
  pages: Record<string, Page | undefined>
  currentPage: Page | undefined
  resourceGroups: Map<string, ResourceGroup>
  resources: Map<string, Resource>
  curatedProjects: Project[]
}

interface StrapiPaginationMeta {
  pagination: {
    page: number
    pageSize: number
    pageCount: number
    total: number
  }
}

const state: State = reactive({
  pages: {},
  currentPage: undefined,
  resourceGroups: new Map(),
  resources: new Map(),
  curatedProjects: [],
})

interface Actions {
  loadPage: (
    uniqueName: string,
    setCurrent?: boolean,
    localeName?: string,
  ) => Promise<Page | undefined>
  setCurrentPage: (page: Page) => void
  loadResourceGroups: () => Promise<void>
  loadResources: () => Promise<void>
  loadCuratedProjects: () => Promise<void>
}

interface Getters {
  currentPage: ComputedRef<Page | undefined>
  filteredResources: (filters?: {
    classification?: string
    category?: string
    mime?: string
  }) => ComputedRef<Resource[]>
  resourceGroups: ComputedRef<Map<string, ResourceGroup>>
  resources: ComputedRef<Map<string, Resource>>
  curatedProjects: ComputedRef<Array<Project>>
}

interface StoreInterface {
  actions: Actions
  getters: Getters
}

/**
 * Generic pagination helper for Strapi API requests
 * Don't confuse pagination pages with the Page schema type!
 * @param fetchPage - Callback function that fetches a single page of data
 * @param pageSize - Number of items per page (default: 100)
 * @returns Array of all paginated results
 */
async function fetchAllPages<T>(
  fetchPage: (
    page: number,
    pageSize: number,
  ) => Promise<{
    data: T[]
    meta: StrapiPaginationMeta
  }>,
  pageSize = 100,
): Promise<T[]> {
  const allResults: T[] = []
  let currentPage = 1
  let hasMorePages = true

  while (hasMorePages) {
    const response = await fetchPage(currentPage, pageSize)

    if (response.data && response.data.length > 0) {
      allResults.push(...response.data)

      // Check if there are more pages
      hasMorePages = currentPage < response.meta.pagination.pageCount
      currentPage++
    } else {
      hasMorePages = false
    }
  }

  return allResults
}

export function useStrapiStore(): StoreInterface {
  const getters = {
    get currentPage(): ComputedRef<Page | undefined> {
      return computed(() => state.currentPage)
    },
    filteredResources(filters?: {
      classification?: string
      category?: string
      mime?: string
    }): ComputedRef<Resource[]> {
      return computed(() => {
        let filtered = Array.from(state.resources.values())

        if (filters) {
          if (filters.classification) {
            filtered = filtered.filter((r) => r.classification === filters.classification)
          }

          if (filters.category) {
            filtered = filtered.filter((r) => r.category === filters.category)
          }

          if (filters.mime) {
            filtered = filtered.filter(
              (r) => r.files && r.files.some((file) => file.mime === filters.mime),
            )
          }
        }

        return filtered
      })
    },
    get resourceGroups(): ComputedRef<Map<string, ResourceGroup>> {
      return computed(() => {
        const groups = new Map<string, ResourceGroup>()
        state.resourceGroups.forEach((g) => groups.set(g.documentId, { ...g, resources: [] }))
        state.resources.forEach((r) => {
          const parentID = r.parent?.documentId ?? ''
          if (groups.has(parentID)) {
            const resource = groups.get(parentID)
            resource!.resources.push(r)
          }
        })
        return groups
      })
    },
    get resources(): ComputedRef<Map<string, Resource>> {
      return computed(() => {
        return state.resources
      })
    },
    get curatedProjects(): ComputedRef<Array<Project>> {
      return computed(() => {
        return state.curatedProjects
      })
    },
  }

  const actions = {
    setCurrentPage(page: Page) {
      state.currentPage = page
    },
    // Load resource groups with pagination
    async loadResourceGroups(): Promise<void> {
      const locale = localStorage.getItem('user-locale') as LocaleCode

      try {
        const allResourceGroups = await fetchAllPages<ResourceGroup>(async (page, pageSize) => {
          const response = await client.collection('collections').find({
            locale,
            populate: {
              fields: ['*'],
            },
            pagination: {
              page,
              pageSize,
            },
          })

          return {
            data: response.data as ResourceGroup[],
            meta: response.meta as StrapiPaginationMeta,
          }
        })

        allResourceGroups.forEach((rg) => state.resourceGroups.set(rg.documentId, rg))
      } catch (error) {
        console.log(`error loading resources: ${(error as Error).toString()}`)
        state.resourceGroups = new Map()
      }
    },
    // Load resources with pagination
    async loadResources(): Promise<void> {
      const locale = localStorage.getItem('user-locale') as LocaleCode

      try {
        const allResources = await fetchAllPages<Resource>(async (page, pageSize) => {
          const response = await client.collection('resources').find({
            locale,
            populate: {
              fields: ['*'],
              files: {
                populate: '*',
              },
              parent: {
                fields: ['id'],
              },
            },
            pagination: {
              page,
              pageSize,
            },
          })

          return {
            data: response.data as Resource[],
            meta: response.meta as StrapiPaginationMeta,
          }
        })

        // Convert Strapi relative URLs to absolute in DEV mode
        if (import.meta.env.DEV) {
          allResources.forEach((resource) => {
            if (resource.files && resource.files.length) {
              resource.files.forEach((file) => {
                file.url = import.meta.env.VITE_STRAPI_URL + file.url
              })
            }
          })
        }

        allResources.forEach((r) => {
          if (r.parent) {
            const resourceGroup = state.resourceGroups.get(r.parent.documentId)
            if (resourceGroup && state.resourceGroups.has(resourceGroup.documentId)) {
              r.parent = state.resourceGroups.get(resourceGroup.documentId)!
            }
          }
          state.resources.set(r.documentId, r)
        })
      } catch (error) {
        console.log(`error loading resources: ${(error as Error).toString()}`)
        state.resources = new Map()
      }
    },
    // Load projects with pagination
    async loadCuratedProjects(): Promise<void> {
      const locale = localStorage.getItem('user-locale') as LocaleCode

      try {
        const allCuratedProjects = await fetchAllPages<CuratedProject>(async (page, pageSize) => {
          const response = await client.collection('projects').find({
            locale,
            populate: {
              fields: ['*'],
              resources: {
                fields: ['id'],
              },
            },
            pagination: {
              page,
              pageSize,
            },
          })

          return {
            data: response.data as CuratedProject[],
            meta: response.meta as StrapiPaginationMeta,
          }
        })

        state.curatedProjects = allCuratedProjects.map((p) => ({
          ...p,
          id: p.documentId,
          lastEdited: new Date(p.updatedAt),
          resources: p.resources.map((rg) => rg.documentId),
          whatFreeText: '',
        }))
      } catch (error) {
        console.log(`error loading resources: ${(error as Error).toString()}`)
        state.resources = new Map()
      }
    },
    // Finds a page by its unique name in the requested locale using Strapi filters.
    // If the page isn't already here, retrieve it from Squidex
    async loadPage(
      uniqueName: string,
      setCurrent = false,
      localeName = '',
    ): Promise<Page | undefined> {
      const locale = localeName || (localStorage.getItem('user-locale') as LocaleCode)
      const pageKey = `${uniqueName.toLowerCase()}-${locale}`
      let page: Page | undefined = state.pages[pageKey]

      if (!page) {
        try {
          const response = await client.collection('pages').find({
            locale,
            filters: { name: { $eqi: uniqueName } },
            populate: {
              fields: ['*'],
              sections: {
                on: {
                  [StrapiComponentName.SimpleBlock]: {
                    populate: {
                      fields: ['*'],
                      media: {
                        populate: '*',
                      },
                      links: {
                        populate: '*',
                      },
                      resources: {
                        populate: '*',
                      },
                    },
                  },
                  [StrapiComponentName.Profiles]: {
                    populate: {
                      profiles: {
                        populate: '*',
                      },
                    },
                  },
                  [StrapiComponentName.Blocks]: {
                    populate: {
                      fields: ['*'],
                      details: {
                        populate: '*',
                      },
                      blocks: {
                        populate: '*',
                      },
                    },
                  },
                  [StrapiComponentName.PageGroups]: {
                    populate: {
                      details: {
                        populate: '*',
                      },
                      pages: {
                        populate: '*',
                      },
                    },
                  },
                  [StrapiComponentName.Resources]: {
                    populate: {
                      resources: {
                        populate: '*',
                      },
                    },
                  },
                },
              },
            },
          })

          if (response.data?.length) {
            page = response.data[0] as Page

            // Convert Strapi relative URLs to absolute
            if (
              import.meta.env.DEV &&
              (import.meta.env.VITE_STRAPI_URL as string)?.includes('localhost')
            ) {
              const simpleBlocks = page.sections.filter(
                (s) => s.__component === StrapiComponentName.SimpleBlock,
              ) as Block[]
              if (simpleBlocks) {
                simpleBlocks.forEach((b) => {
                  if (b.media && b.media.length) {
                    b.media.forEach((m) => (m.url = import.meta.env.VITE_STRAPI_URL + m.url))
                  }
                })
              }

              const blockListList = page.sections.filter(
                (s) => s.__component === StrapiComponentName.Blocks,
              ) as BlockList[]
              blockListList.forEach((list) => {
                list.blocks.forEach((b) => {
                  if (b.media && b.media.length) {
                    b.media.forEach((m) => (m.url = import.meta.env.VITE_STRAPI_URL + m.url))
                  }
                })
                if (list.details.media)
                  if (list.details.media && list.details.media.length) {
                    list.details.media.forEach(
                      (m) => (m.url = import.meta.env.VITE_STRAPI_URL + m.url),
                    )
                  }
              })

              const profileList = page.sections.find(
                (s) => s.__component === StrapiComponentName.Profiles,
              ) as ProfileList | undefined
              if (profileList) {
                profileList.profiles.forEach((p) => {
                  if (p.image) p.image.url = import.meta.env.VITE_STRAPI_URL + p.image.url
                })
              }

              const resourceList = page.sections.find(
                (s) => s.__component === StrapiComponentName.Resources,
              ) as ResourceList | undefined
              if (resourceList) {
                resourceList.resources.forEach((r) => {
                  if (r.files)
                    r.files.forEach((f) => {
                      f.url = import.meta.env.VITE_STRAPI_URL + f.url
                    })
                })
              }
            }
            state.pages[pageKey] = page
          }
        } catch (error) {
          console.log((error as Error).message)
          state.pages[pageKey] = undefined
        }
      }

      if (setCurrent) state.currentPage = page
      if (!page) state.currentPage = undefined
      return page
    },
  }

  return { actions, getters }
}

export default useStrapiStore
